/**
 * One tenancy's reconciliation detail.
 *
 * Moved verbatim out of `BexioReconciliationPage` so the page stays an orchestrator
 * (CLAUDE.md Regel 48) — the JSX, the labels and the field sources are unchanged.
 * The `selected ? … : null` guard stayed at the call site, so this component is only
 * ever rendered with a row.
 *
 * The point of the whole page lives here: WHICH Bexio invoices went into this
 * tenancy's figures, listed one by one from `counted_documents`, plus the mapped
 * invoices the service left out of every total, so "why is this tenancy at zero" is
 * answerable without leaving the page.
 */
import React from "react";
import { AlertTriangle } from "lucide-react";

import {
  DrawerField,
  DrawerSection,
} from "../workspace/BexioDetailDrawer";
import { INVOICE_STATUS_LABEL, fmtDateCH, fmtMoney, mapLabel } from "../bexioFormat";

const DASH = "—";

export default function BexioReconciliationDrawerBody({
  row,
  currency,
  hasHint,
  isFlagged,
  coveragePct,
}) {
  return (
    <>
          <DrawerSection title="Mietverhältnis">
            <DrawerField
              label="Referenz"
              value={row.tenancy_reference_number}
              wide
            />
            <DrawerField label="Mieter" value={row.tenant_display_name} wide />
            <DrawerField label="Einheit" value={row.unit_label} />
            <DrawerField label="Zimmer" value={row.room_label} />
            <DrawerField label="Status" value={row.tenancy_status} />
            <DrawerField label="Mietverhältnis-ID" value={row.tenancy_id} mono />
          </DrawerSection>

          <DrawerSection title="Beträge">
            <DrawerField
              label="Fakturiert (berücksichtigt)"
              value={fmtMoney(row.invoice_total_counted, currency)}
            />
            <DrawerField
              label="Bezahlt"
              value={fmtMoney(row.paid_total_invoice_level, currency)}
            />
            <DrawerField
              label="Offen (fällig)"
              value={fmtMoney(row.open_due, currency)}
            />
            <DrawerField
              label="Offen (nicht fällig / ohne Fälligkeit)"
              value={fmtMoney(row.open_not_yet_due_or_no_due_date, currency)}
            />
            <DrawerField
              label="Offen gesamt"
              value={fmtMoney(row.open_total, currency)}
              wide
            />
          </DrawerSection>

          {/* Which invoices were counted, individually — the point of the whole
              page. Excluded ones are reported as counts beside them, because the
              response gives those per tenancy but not as documents. */}
          <section className="rounded-lg border border-border p-4">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Berücksichtigte Rechnungen ({row.counted_invoices})
            </h3>
            {Array.isArray(row.counted_documents) &&
            row.counted_documents.length > 0 ? (
              <ul className="space-y-2">
                {row.counted_documents.map((d) => (
                  <li
                    key={d.bexio_invoice_id}
                    className="rounded-md border border-border p-2.5 text-sm"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="font-medium text-foreground">
                        {d.document_nr || `#${d.bexio_invoice_id}`}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {mapLabel(INVOICE_STATUS_LABEL, d.bexio_status)}
                      </span>
                    </div>
                    <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <div>
                        <dt className="inline text-muted-foreground">Betrag: </dt>
                        <dd className="inline text-foreground">
                          {fmtMoney(d.total, currency)}
                        </dd>
                      </div>
                      <div>
                        <dt className="inline text-muted-foreground">Bezahlt: </dt>
                        <dd className="inline text-foreground">
                          {fmtMoney(d.total_paid, currency)}
                        </dd>
                      </div>
                      <div>
                        <dt className="inline text-muted-foreground">Offen: </dt>
                        <dd className="inline text-foreground">
                          {fmtMoney(d.total_remaining, currency)}
                        </dd>
                      </div>
                      <div>
                        <dt className="inline text-muted-foreground">Fällig: </dt>
                        <dd className="inline text-foreground">
                          {/* `fmtDateCH`, not `formatDateTime`: `due_date` is a
                              DATE in the contract, and the datetime formatter
                              rendered it as "28.08.2026, 02:00" — a time of day
                              that does not exist in the data. `formatDateTime`
                              stays for the sync timestamp, which really is one. */}
                          {d.due_date ? fmtDateCH(d.due_date) : DASH}
                        </dd>
                      </div>
                    </dl>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                Keine Rechnung dieses Mietverhältnisses wurde berücksichtigt.
              </p>
            )}
          </section>

          {/* Mapped invoices the service left out of every total, per its own
              per-tenancy counters. Shown because "why is this tenancy at zero"
              is otherwise unanswerable from the page. */}
          {hasHint(row) ? (
            <section className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
              <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
                <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5" />
                Hinweise
              </h3>
              <ul className="list-inside list-disc space-y-1">
                {Number(row.mapped_draft_invoices) > 0 ? (
                  <li>
                    {row.mapped_draft_invoices} zugeordnete Rechnung(en) im
                    Entwurf — nicht in den Summen.
                  </li>
                ) : null}
                {Number(row.mapped_canceled_invoices) > 0 ? (
                  <li>
                    {row.mapped_canceled_invoices} zugeordnete Rechnung(en)
                    storniert — nicht in den Summen.
                  </li>
                ) : null}
                {Number(row.mapped_unknown_status_invoices) > 0 ? (
                  <li>
                    {row.mapped_unknown_status_invoices} zugeordnete
                    Rechnung(en) mit unbekanntem Status — nicht in den Summen.
                  </li>
                ) : null}
                {/* Same shape as the three above — a COUNT with its reason, not a
                    document list, because the response gives these per tenancy and
                    not as documents. It answers the question the drawer otherwise
                    leaves open: why "Berücksichtigte Rechnungen" is short. */}
                {Number(row.mapped_provider_missing_invoices) > 0 ? (
                  <li>
                    {row.mapped_provider_missing_invoices} zugeordnete
                    Rechnung(en) in Bexio nicht mehr vorhanden — nicht in den
                    Summen.
                  </li>
                ) : null}
                {isFlagged(row) ? (
                  <li>
                    Dieses Mietverhältnis erscheint in den offenen Hinweisen oben.
                  </li>
                ) : null}
              </ul>
            </section>
          ) : null}

          <p className="text-xs text-muted-foreground">
            Bexio ist führend für Rechnungs- und Zahlungsstatus. Alle Beträge
            stammen aus dem Bexio-Spiegel; diese Seite verändert in Bexio nichts.
            {coveragePct !== null ? ` Zuordnungsgrad org-weit: ${coveragePct}%.` : ""}
          </p>
    </>
  );
}
